import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { firstValueFrom, of, race } from 'rxjs';
import { take } from 'rxjs/operators';
import { selectGlobalFiltersState } from '@app/store/reducers';
import {
  DEFAULT_GLOBAL_DATE_FILTER_SPAN_FALLBACK,
  initialState as initialGlobalFilterState,
} from '@app/store/global-filter.reducer';
import {
  resetGlobalFiltersToDefault,
  setGlobalFilters,
  setRelativeGlobalFilter,
} from '@app/store/global-filter.actions';
import { selectDefaultGlobalDateFilterSpan } from '@main/settings/store/specific-setting.selectors';
import type { GlobalDateFilterDialogResult } from './global-date-filter-dialog.component';

function normalizeDateBound(
  value: DateTime | string | null | undefined,
  boundOf: 'startOf' | 'endOf',
): string | null {
  if (value == null) {
    return null;
  }

  return typeof value === 'string' ? value : value[boundOf]('day').toString();
}

/**
 * Shared entry point for opening the global date filter dialog and applying
 * its result to the store. Both the status bar trigger and the "Hello,
 * <name>!" menu item delegate here so the afterClosed dispatch matrix
 * (RESET / relative / absolute) lives in one place.
 */
@Injectable({ providedIn: 'root' })
export class GlobalDateFilterDialogService {
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  async open(): Promise<void> {
    const [{ GlobalDateFilterDialogComponent }, globalFilters, defaultSpan] = await Promise.all([
      import('./global-date-filter-dialog.component'),
      firstValueFrom(this.store.select(selectGlobalFiltersState).pipe(take(1))),
      firstValueFrom(
        // `store.pipe(selectDefaultGlobalDateFilterSpan)` never emits until the
        // setting has resolved and never completes, so a plain `startWith` +
        // `take(1)` would always win with the fallback (it is synchronous and
        // unsubscribes the race immediately). `race` instead prefers the real
        // value whenever the Store already holds it (it emits synchronously,
        // before `of(fallback)` is even subscribed) and falls back only when
        // the setting genuinely has not resolved yet.
        race(
          this.store.pipe(selectDefaultGlobalDateFilterSpan),
          of(DEFAULT_GLOBAL_DATE_FILTER_SPAN_FALLBACK),
        ).pipe(take(1)),
      ),
    ]);

    const snapshot = globalFilters ?? initialGlobalFilterState;

    const result = await firstValueFrom(
      this.dialog.open(GlobalDateFilterDialogComponent, {
        data: {
          globalFilters: snapshot,
          defaultSpan: defaultSpan.toISO(),
        },
      }).afterClosed(),
    );

    this.handleResult(result);
  }

  private handleResult(result: GlobalDateFilterDialogResult | undefined | null): void {
    if (result == null) {
      return;
    }

    if (result === 'RESET') {
      this.store.dispatch(resetGlobalFiltersToDefault());
      return;
    }

    if (result.mode === 'relative') {
      this.store.dispatch(setRelativeGlobalFilter({ relativeSpan: result.relativeSpan }));
    } else {
      this.store.dispatch(setGlobalFilters({
        startAtOrAfter: normalizeDateBound(result.start, 'startOf'),
        endAtOrBefore: normalizeDateBound(result.end, 'endOf'),
      }));
    }
  }
}
